import { createContext, useContext, useLayoutEffect, useMemo, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'

export type ItemMetadataKind = 'title' | 'description' | 'details'

type ItemMetadataEntry = {
  key: symbol
  id: string
}

type ItemMetadataEntries = Record<ItemMetadataKind, readonly ItemMetadataEntry[]>

export type ItemMetadataSnapshot = {
  titleIds: readonly string[]
  descriptionIds: readonly string[]
  detailsIds: readonly string[]
}

export type ItemMetadataScope = {
  snapshot: ItemMetadataSnapshot
  register: (kind: ItemMetadataKind, id: string) => () => void
}

const EMPTY_ITEM_METADATA_ENTRIES: ItemMetadataEntries = {
  title: [],
  description: [],
  details: [],
}

/**
 * Collects the ids of the `Item.Title`, `Item.Description` and `Item.Details` rendered in a row. `Item.Root` creates the
 * scope, each slot registers its id on mount, and a row-level control (see `ControlItem`) reads the snapshot to wire its
 * `aria-labelledby` and `aria-describedby` without the caller passing ids. `undefined` means no row scope: slots then
 * register nothing.
 */
export const ItemMetadataContext = createContext<ItemMetadataScope | undefined>(undefined)

type RegisterItemMetadata = (kind: ItemMetadataKind, id: string) => () => void

function createRegisterItemMetadata(setEntries: Dispatch<SetStateAction<ItemMetadataEntries>>): RegisterItemMetadata {
  return (kind, id) => {
    const key = Symbol(kind)
    let active = true
    setEntries((entries) => ({ ...entries, [kind]: [...entries[kind], { key, id }] }))

    return () => {
      if (!active) return
      active = false
      setEntries((entries) => ({ ...entries, [kind]: entries[kind].filter((entry) => entry.key !== key) }))
    }
  }
}

/** Creates the metadata scope of one row. Called by `Item.Root`. */
export function useCreateItemMetadataScope(): ItemMetadataScope {
  const [entries, setEntries] = useState(EMPTY_ITEM_METADATA_ENTRIES)
  const register = useMemo(() => createRegisterItemMetadata(setEntries), [setEntries])
  const snapshot = useMemo(
    () => ({
      titleIds: entries.title.map((entry) => entry.id),
      descriptionIds: entries.description.map((entry) => entry.id),
      detailsIds: entries.details.map((entry) => entry.id),
    }),
    [entries],
  )

  return useMemo(() => ({ snapshot, register }), [snapshot, register])
}

/** Reads the ids registered in the current row, or `undefined` outside a row. */
export function useItemMetadata(): ItemMetadataSnapshot | undefined {
  return useContext(ItemMetadataContext)?.snapshot
}

/** Registers a slot's id in the current row for as long as the slot is mounted. */
export function useRegisterItemMetadata(kind: ItemMetadataKind, id: string): void {
  const scope = useContext(ItemMetadataContext)
  const register = scope?.register

  useLayoutEffect(
    function registerItemMetadata() {
      if (register === undefined) return undefined
      return register(kind, id)
    },
    [register, kind, id],
  )
}
